import React, { useMemo } from 'react'
import { LayersPanel as ModularLayersPanel } from '../../molecules/LayersPanel'
import { LegendPanel as ModularLegendPanel } from '../../molecules/LegendPanel'
import { MapLayer } from './Map.types'
import { MAP_STYLES } from '../../../config/mapStylesConfig'
import type { LayerGroup } from '../../molecules/LayersPanel/LayersPanel.types'

interface MapPanelsProps {
  // Layers Panel
  showLayerSelector: boolean
  CustomLayersPanel?: React.ComponentType<any>
  showBuiltInLayersPanel: boolean
  layersPanelOpen: boolean
  setLayersPanelOpen: (open: boolean) => void
  mapLayers: MapLayer[]
  onLayerToggle: (layerId: string, enabled: boolean) => void
  onLayerSelect?: (layerId: string, value?: string) => void
  onLayerOpacityChange?: (layerId: string, opacity: number) => void
  currentMapStyle?: string
  selectedThematicLayer: string
  setLegendPanelOpen: (open: boolean) => void
  /** Optional dynamic layer groups (e.g., from section-based data) */
  dynamicLayerGroups?: LayerGroup[]
  /** Optional custom controls to render for specific layers */
  renderCustomControls?: (selectedLayerId: string) => React.ReactNode

  // Legend Panel
  showLegend: boolean
  CustomLegendPanel?: React.ComponentType<any>
  showBuiltInLegendPanel: boolean
  legendPanelOpen: boolean
  legendConfigs: Record<string, any>
}

export const MapPanels: React.FC<MapPanelsProps> = ({
  showLayerSelector,
  CustomLayersPanel,
  showBuiltInLayersPanel,
  layersPanelOpen,
  setLayersPanelOpen,
  mapLayers,
  onLayerToggle,
  onLayerSelect,
  onLayerOpacityChange,
  currentMapStyle = 'mapbox-standard',
  selectedThematicLayer,
  setLegendPanelOpen,
  dynamicLayerGroups = [],
  renderCustomControls,

  showLegend,
  CustomLegendPanel,
  showBuiltInLegendPanel,
  legendPanelOpen,
  legendConfigs,
}) => {
  const handleLayerSelect = (layerId: string, value: string) => {
    if (layerId === 'map-style-select') {
      // Handle map style change - pass ONLY the style ID (not the layerId)
      // This ensures handleMapStyleChange receives (styleId, undefined) and takes the correct branch
      onLayerSelect?.(value, undefined)
    } else if (layerId === 'theme-select') {
      setLegendPanelOpen(value !== '')
    } else {
      // Handle dynamic layer radio selections (e.g., demographics-choropleth)
      // Pass both the group ID and the selected layer ID
      onLayerSelect?.(layerId, value)
    }
  }

  // Memoize layerGroups to ensure dropdown re-renders when currentMapStyle changes
  const layerGroups = useMemo(
    () => [
      // Dynamic layer groups (section-based) appear first
      ...(dynamicLayerGroups || []),
      {
        id: 'map-style',
        title: 'Map Base',
        groupType: 'base' as const,
        groupRenderType: 'exclusive-dropdown' as const,
        icon: 'map' as const,
        defaultExpanded: true,
        layers: [
          {
            id: 'map-style-select',
            label: 'Choose Map Style',
            enabled: true,
            type: 'radio' as const,
            renderAs: 'select' as const,
            selectedValue: currentMapStyle,
            options: MAP_STYLES.map((style) => ({
              value: style.id,
              label: style.label,
              description: style.description,
            })),
          },
        ],
      },
      {
        id: 'land-property',
        title: 'Land',
        groupType: 'base' as const,
        groupRenderType: 'multiple-checkbox' as const,
        icon: 'layers' as const,
        defaultExpanded: false,
        layers: mapLayers
          .filter((layer) =>
            ['parcel-boundaries', 'land-dimension-metrics'].includes(layer.id)
          )
          .map((layer) => ({
            id: layer.id,
            label: layer.name,
            enabled: layer.enabled,
            disabled: layer.disabled,
            disabledReason: layer.disabledReason,
          })),
      },
      {
        id: 'building-property',
        title: 'Building',
        groupType: 'base' as const,
        groupRenderType: 'multiple-checkbox' as const,
        icon: 'building' as const,
        defaultExpanded: false,
        layers: mapLayers
          .filter((layer) =>
            [
              'building-footprints',
              'building-3d',
              'building-dimension-metrics',
            ].includes(layer.id)
          )
          .map((layer) => ({
            id: layer.id,
            label: layer.name,
            enabled: layer.enabled,
            disabled: layer.disabled,
            disabledReason: layer.disabledReason,
          })),
      },
      {
        id: 'map-features',
        title: 'Map Features',
        groupType: 'base' as const,
        groupRenderType: 'multiple-checkbox' as const,
        icon: 'route' as const,
        defaultExpanded: false,
        layers: mapLayers
          .filter((layer) =>
            ['streets', 'labels', 'terrain', 'contours'].includes(layer.id)
          )
          .map((layer) => ({
            id: layer.id,
            label: layer.name,
            enabled: layer.enabled,
            disabled: layer.disabled,
            disabledReason: layer.disabledReason,
          })),
      },
    ],
    [currentMapStyle, dynamicLayerGroups, mapLayers]
  )

  return (
    <>
      {/* Layers Panel */}
      {showLayerSelector &&
        (CustomLayersPanel ? (
          <CustomLayersPanel />
        ) : (
          showBuiltInLayersPanel && (
            <ModularLayersPanel
              active={layersPanelOpen}
              onClose={() => setLayersPanelOpen(false)}
              enableSearch={true}
              renderCustomControls={renderCustomControls}
              layerGroups={layerGroups}
              onLayerToggle={onLayerToggle}
              onLayerSelect={handleLayerSelect}
              onLayerOpacityChange={onLayerOpacityChange}
            />
          )
        ))}

      {/* Legend Panel */}
      {showLegend &&
        (CustomLegendPanel ? (
          <CustomLegendPanel />
        ) : (
          showBuiltInLegendPanel && (
            <ModularLegendPanel
              active={legendPanelOpen && selectedThematicLayer !== ''}
              legend={
                selectedThematicLayer &&
                legendConfigs[
                  selectedThematicLayer as keyof typeof legendConfigs
                ]
                  ? (() => {
                      const config =
                        legendConfigs[
                          selectedThematicLayer as keyof typeof legendConfigs
                        ]
                      if (config.type === 'gradient') {
                        return {
                          type: 'gradient' as const,
                          title: config.title,
                          min: config.labels[0],
                          max: config.labels[config.labels.length - 1],
                          unit: config.unit,
                          colors: ['#2ECC71', '#F1C40F', '#E67E22', '#E74C3C'],
                          labels: config.labels,
                        }
                      } else if (config.type === 'categories') {
                        return {
                          type: 'category' as const,
                          title: config.title,
                          items: config.items.map(
                            (item: any, index: number) => ({
                              id: `cat-${index}`,
                              label: item.label,
                              color: item.color,
                              active: true,
                            })
                          ),
                        }
                      }
                      return null
                    })()
                  : null
              }
            />
          )
        ))}
    </>
  )
}
